import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import axios from 'axios';
import { useClinic } from '../../contexts/ClinicContext';

const { FiSave, FiUpload, FiClock, FiDollarSign, FiGlobe } = FiIcons;

const ClinicSettings = () => {
  const { refreshClinicInfo } = useClinic();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState('');
  const [faviconFile, setFaviconFile] = useState(null);
  const [letterheadPreview, setLetterheadPreview] = useState('');
  const [letterheadFile, setLetterheadFile] = useState(null);
  const [formData, setFormData] = useState({
    clinic_name: 'EasyAI Dental Center',
    address: '',
    phone: '',
    email: '',
    website: '',
    business_hours: {
      monday: { open: '08:00', close: '20:00' },
      tuesday: { open: '08:00', close: '20:00' },
      wednesday: { open: '08:00', close: '20:00' },
      thursday: { open: '08:00', close: '20:00' },
      friday: { open: '14:00', close: '20:00' },
      saturday: { open: '14:00', close: '20:00' },
      sunday: { open: '08:00', close: '20:00' }
    },
    card_processing_fee: 2.8,
    currency: 'QR'
  });

  useEffect(() => {
    fetchClinicSettings();
  }, []);

  const fetchClinicSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/clinic');

      // Parse business_hours JSON if it's a string
      let businessHours = response.data.business_hours;
      if (typeof businessHours === 'string') {
        businessHours = JSON.parse(businessHours);
      }

      setFormData({
        ...response.data,
        business_hours: businessHours || formData.business_hours
      });

      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      
      if (response.data.favicon_url) {
        setFaviconPreview(response.data.favicon_url);
      }

      if (response.data.letterhead_url) {
        setLetterheadPreview(response.data.letterhead_url);
      }
    } catch (error) {
      console.error('Error fetching clinic settings:', error);
      setError('Failed to load clinic settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setFormData({
      ...formData,
      business_hours: {
        ...formData.business_hours,
        [day]: {
          ...formData.business_hours[day],
          [field]: value
        }
      }
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLetterheadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLetterheadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLetterheadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    setSuccess(false);

    try {
      // Create FormData object for file upload
      const data = new FormData();

      // Add all form fields
      data.append('clinic_name', formData.clinic_name);
      data.append('address', formData.address);
      data.append('phone', formData.phone);
      data.append('email', formData.email);
      data.append('website', formData.website);
      data.append('business_hours', JSON.stringify(formData.business_hours));
      data.append('card_processing_fee', formData.card_processing_fee);
      data.append('currency', formData.currency);

      // Add logo file if selected
      if (logoFile) {
        data.append('logo', logoFile);
      }
      
      // Add favicon file if selected
      if (faviconFile) {
        data.append('favicon', faviconFile);
      }

      // Add letterhead file if selected
      if (letterheadFile) {
        data.append('letterhead', letterheadFile);
      }

      await axios.put('/api/settings/clinic', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      
      // Refresh clinic info in context
      refreshClinicInfo();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving clinic settings:', error);
      setError(error.response?.data?.error || 'Failed to save clinic settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-grey-900">Clinic Information</h2>
          <p className="mt-1 text-sm text-grey-600">
            Configure your clinic details, business hours and payment settings.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">Settings saved successfully!</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Clinic Details */}
          <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
            <div className="px-4 py-5 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <label htmlFor="clinic_name" className="block text-sm font-medium text-grey-700">
                    Clinic Name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="clinic_name"
                      id="clinic_name"
                      required
                      className="shadow-sm focus:ring-maroon-500 focus:border-maroon-500 block w-full sm:text-sm border-grey-300 rounded-md"
                      value={formData.clinic_name}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="logo" className="block text-sm font-medium text-grey-700">
                    Clinic Logo
                  </label>
                  <div className="mt-1 flex items-center">
                    <div className="flex-shrink-0 h-16 w-16 border border-grey-300 rounded-md overflow-hidden bg-grey-100">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-grey-400">
                          <SafeIcon icon={FiIcons.FiImage} className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <div className="ml-5">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-maroon-600 hover:text-maroon-500 focus-within:outline-none"
                      >
                        <span className="flex items-center space-x-1">
                          <SafeIcon icon={FiUpload} className="w-4 h-4" />
                          <span>Upload a file</span>
                        </span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleLogoChange}
                        />
                      </label>
                      <p className="text-xs text-grey-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                </div>
                
                <div className="sm:col-span-6">
                  <label htmlFor="favicon" className="block text-sm font-medium text-grey-700">
                    Favicon (Browser Tab Icon)
                  </label>
                  <div className="mt-1 flex items-center">
                    <div className="flex-shrink-0 h-16 w-16 border border-grey-300 rounded-md overflow-hidden bg-grey-100">
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="Favicon preview" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-grey-400">
                          <SafeIcon icon={FiIcons.FiImage} className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <div className="ml-5">
                      <label
                        htmlFor="favicon-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-maroon-600 hover:text-maroon-500 focus-within:outline-none"
                      >
                        <span className="flex items-center space-x-1">
                          <SafeIcon icon={FiUpload} className="w-4 h-4" />
                          <span>Upload favicon</span>
                        </span>
                        <input
                          id="favicon-upload"
                          name="favicon-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleFaviconChange}
                        />
                      </label>
                      <p className="text-xs text-grey-500 mt-1">PNG, ICO, SVG recommended</p>
                      <p className="text-xs text-grey-500">If not provided, logo will be used as favicon</p>
                    </div>
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="letterhead" className="block text-sm font-medium text-grey-700">
                    Invoice Letterhead
                  </label>
                  <div className="mt-1 flex items-center">
                    <div className="flex-shrink-0 h-40 w-32 border border-grey-300 rounded-md overflow-hidden bg-grey-100">
                      {letterheadPreview ? (
                        <img src={letterheadPreview} alt="Letterhead preview" className="h-full w-full object-contain" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-grey-400">
                          <SafeIcon icon={FiIcons.FiFile} className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <div className="ml-5">
                      <label
                        htmlFor="letterhead-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-maroon-600 hover:text-maroon-500 focus-within:outline-none"
                      >
                        <span className="flex items-center space-x-1">
                          <SafeIcon icon={FiUpload} className="w-4 h-4" />
                          <span>Upload letterhead</span>
                        </span>
                        <input
                          id="letterhead-upload"
                          name="letterhead-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleLetterheadChange}
                        />
                      </label>
                      <p className="text-xs text-grey-500 mt-1">PNG, JPG recommended (A4 size)</p>
                      <p className="text-xs text-grey-500">This will be used as background for printed invoices</p>
                    </div>
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="address" className="block text-sm font-medium text-grey-700">
                    Address
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      className="shadow-sm focus:ring-maroon-500 focus:border-maroon-500 block w-full sm:text-sm border-grey-300 rounded-md"
                      value={formData.address}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="phone" className="block text-sm font-medium text-grey-700">
                    Phone Number
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      className="shadow-sm focus:ring-maroon-500 focus:border-maroon-500 block w-full sm:text-sm border-grey-300 rounded-md"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="email" className="block text-sm font-medium text-grey-700">
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="shadow-sm focus:ring-maroon-500 focus:border-maroon-500 block w-full sm:text-sm border-grey-300 rounded-md"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="website" className="block text-sm font-medium text-grey-700">
                    Website
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiGlobe} className="text-grey-400 h-5 w-5" />
                    </div>
                    <input
                      type="text"
                      name="website"
                      id="website"
                      className="focus:ring-maroon-500 focus:border-maroon-500 block w-full pl-10 sm:text-sm border-grey-300 rounded-md"
                      placeholder="www.example.com"
                      value={formData.website}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="currency" className="block text-sm font-medium text-grey-700">
                    Currency
                  </label>
                  <div className="mt-1">
                    <select
                      id="currency"
                      name="currency"
                      className="shadow-sm focus:ring-maroon-500 focus:border-maroon-500 block w-full sm:text-sm border-grey-300 rounded-md"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="QR">QR - Qatari Riyal</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="AED">AED - UAE Dirham</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiClock} className="h-5 w-5 text-grey-400 mr-2" />
                <h3 className="text-lg font-medium leading-6 text-grey-900">Business Hours</h3>
              </div>
              <div className="space-y-4">
                {Object.keys(formData.business_hours).map((day) => (
                  <div key={day} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                    <div className="sm:col-span-1">
                      <label className="block text-sm font-medium text-grey-700 capitalize">
                        {day}
                      </label>
                    </div>
                    <div className="sm:col-span-2 flex space-x-4">
                      <div className="flex-1">
                        <label htmlFor={`${day}-open`} className="block text-xs text-grey-500">
                          Open
                        </label>
                        <input
                          type="time"
                          id={`${day}-open`}
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.business_hours[day].open}
                          onChange={(e) => handleBusinessHoursChange(day, 'open', e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={`${day}-close`} className="block text-xs text-grey-500">
                          Close
                        </label>
                        <input
                          type="time"
                          id={`${day}-close`}
                          className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                          value={formData.business_hours[day].close}
                          onChange={(e) => handleBusinessHoursChange(day, 'close', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Payment Settings */}
          <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-grey-400 mr-2" />
                <h3 className="text-lg font-medium leading-6 text-grey-900">Payment Settings</h3>
              </div>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label
                    htmlFor="card_processing_fee"
                    className="block text-sm font-medium text-grey-700"
                  >
                    Card Processing Fee (%)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      name="card_processing_fee"
                      id="card_processing_fee"
                      className="focus:ring-maroon-500 focus:border-maroon-500 block w-full pr-12 sm:text-sm border-grey-300 rounded-md"
                      value={formData.card_processing_fee}
                      onChange={handleInputChange}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-grey-500 sm:text-sm">%</span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-grey-500">
                    This fee will be deducted from doctor payouts when patients pay with cards.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ClinicSettings;